import {
  NestedUser,
  NestedPlan,
  DateRange,
  GroupedCommissions,
  KeyDeal,
  CommissionBasedOn,
  SignedInCompany,
  CalculationDeal,
  NestedDeal,
  Condition,
  YouniumMetrics,
} from "@revelate/types";
import { criteriaMet } from "./accelerators";
import {
  CommissionStatus,
  CommissionUser,
  NestedCommission,
  NestedCompany,
  Scope,
  TODO,
} from "@revelate/types";
import {
  format,
  startOfDay,
  endOfDay,
  startOfYear,
  addMonths,
  isSameMonth,
  isBefore,
  isAfter,
} from "date-fns";
import { UTCDate } from "@date-fns/utc";
import { enUS } from "date-fns/locale";
import { getUserIdsForTeam } from "./teams";
import {
  filterByDealRole,
  // getDealOwnerId,
  filterDeals,
  getDealValue,
  getDealValueFromCommissions,
} from "./deals";
import {
  capitalizeFirstLetter,
  generateCompositeKey,
  generateCompositeKeyHash,
  // generateUniqueCommissionsKey,
  getCurrencyFormatted,
  getFullName,
  getPlanStartEndDates,
  shouldCalculate,
} from "@revelate/utils";
import {
  getExchangeRateForCurrencyId,
  getExchangeRateForDeal,
} from "./currencies";
import { OperationTimer, logOperation } from "./performance";
import { getHumanReadableConditionName } from "./conditions";

const getDealValueFromDataField = (
  deal: CalculationDeal,
  data_field = "value",
  isProjected: boolean = false
) => {
  let value = deal.value ?? 0;

  switch (data_field) {
    case "value":
      value = deal.value;
      break;
    case "onboarding_value":
      value = deal.onboarding_value ?? 0;
      break;
    default:
      value = (deal as TODO)[data_field] ?? 0;
  }

  const projectionMultiplier = isProjected
    ? (deal.likelihood_to_close ?? 1)
    : 1;
  return value * projectionMultiplier;
};

type AboveBelowResults = {
  totalAttainment: number;
  commissionAmount: number;
  commissionBasis: number;
  commissionDeals: CalculationDeal[]; // Deals which were used in the above/below total
  aboveTarget: number;
  belowTarget: number;
};

/**
 * @deprecated
 * Above/below system deprecated in favor of tiers
 */
const getAboveBelowCommissionBasis = (
  data_field: string,
  acceleratorCommissionBasedOn: CommissionBasedOn,
  acceleratorRate: number,
  targetValue: number,
  criteriaMetDeals: CalculationDeal[],
  quotaDeals: CalculationDeal[],
  company: NestedCompany, // Needed for exchange rate
  dealValueCache: Map<string, number>,
  exchangeRates: Map<string, number>,
  isProjected: boolean = false
): AboveBelowResults => {
  // Accumulate the total by going through the quota deals in chronological order
  // And for each deal, check if it met the criteria
  // If yes, then apply the above/below logic for it

  const criteriaMetDealIds = new Set(criteriaMetDeals.map((d) => d.id));

  // Early return to avoid calculations if no deals passed the criteria
  // (although this scenario should already be handled before this and not reach this point)
  if (criteriaMetDealIds.size === 0) {
    return {
      totalAttainment: 0,
      commissionAmount: 0,
      commissionBasis: 0,
      commissionDeals: [],
      aboveTarget: 0,
      belowTarget: 0,
    };
  }

  let totalDealValue = 0;
  let aboveTarget = 0;
  let belowTarget = 0;
  let totalCommissionAmount = 0;
  let totalCommissionBasis = 0;
  let commissionDeals: CalculationDeal[] = [];

  for (const deal of quotaDeals) {
    // const dealValue =
    //   getExchangeRateForDeal(company, deal) *
    //   getDealValueFromDataField(deal, data_field ?? "value", isProjected);

    const dealValue = getCachedDealValue(
      deal,
      data_field,
      company,
      dealValueCache,
      exchangeRates,
      isProjected
    );

    // Target already crossed
    if (totalDealValue > targetValue) {
      aboveTarget += dealValue;

      if (
        criteriaMetDealIds.has(deal.id) &&
        acceleratorCommissionBasedOn === "deal_value_above_target"
      ) {
        const dealCommission = dealValue * acceleratorRate;
        totalCommissionAmount += dealCommission;
        totalCommissionBasis += dealValue;
        commissionDeals.push(deal);
      }
    }

    // Target will be met or crossed with this deal
    else if (totalDealValue + dealValue >= targetValue) {
      const partialDealValueAboveTarget =
        totalDealValue + dealValue - targetValue;
      const partialDealValueBelowTarget =
        dealValue - partialDealValueAboveTarget;

      aboveTarget += partialDealValueAboveTarget;
      belowTarget += partialDealValueBelowTarget;

      if (criteriaMetDealIds.has(deal.id)) {
        if (acceleratorCommissionBasedOn === "deal_value_above_target") {
          const dealCommission = partialDealValueAboveTarget * acceleratorRate;
          totalCommissionAmount += dealCommission;
          totalCommissionBasis += partialDealValueAboveTarget;
          commissionDeals.push(deal);
        } else if (acceleratorCommissionBasedOn === "deal_value_below_target") {
          const dealCommission = partialDealValueBelowTarget * acceleratorRate;
          totalCommissionAmount += dealCommission;
          totalCommissionBasis += partialDealValueBelowTarget;
          commissionDeals.push(deal);
        }
      }
    }

    // Below target
    else {
      belowTarget += dealValue;

      if (
        criteriaMetDealIds.has(deal.id) &&
        acceleratorCommissionBasedOn === "deal_value_below_target"
      ) {
        const dealCommission = dealValue * acceleratorRate;
        totalCommissionAmount += dealCommission;
        totalCommissionBasis += dealValue;
        commissionDeals.push(deal);
      }
    }

    // Accumulate
    totalDealValue += dealValue;
  }

  return {
    totalAttainment: totalDealValue,
    commissionAmount: totalCommissionAmount,
    commissionBasis: totalCommissionBasis,
    commissionDeals,
    aboveTarget,
    belowTarget,
  };
};

type TierResults = {
  totalAttainment: number;
  commissionAmount: number;
  commissionBasis: number;
  commissionDeals: CalculationDeal[];
  belowTier: number;
  withinTier: number;
  aboveTier: number;
};

const getTierCommissionBasis = (
  data_field: string,
  condition: Condition, // Tier information here
  planId: number,
  acceleratorRate: number,
  targetValue: number,
  criteriaMetDeals: CalculationDeal[],
  quotaDeals: CalculationDeal[],
  company: NestedCompany, // Needed for exchange rate
  dealValueCache: Map<string, number>,
  exchangeRates: Map<string, number>,
  isProjected: boolean = false
): TierResults => {
  // Accumulate the total by going through the quota deals in chronological order
  // And for each deal, check if it met the criteria
  // If yes, then apply the tier logic for it

  const criteriaMetDealIds = new Set(criteriaMetDeals.map((d) => d.id));

  // Early return to avoid calculations if no deals passed the criteria
  // (although this scenario should already be handled before this and not reach this point)
  if (criteriaMetDealIds.size === 0) {
    return {
      totalAttainment: 0,
      commissionAmount: 0,
      commissionBasis: 0,
      commissionDeals: [],
      belowTier: 0,
      withinTier: 0,
      aboveTier: 0,
    };
  }

  let totalDealValue = 0;
  let belowTier = 0;
  let withinTier = 0;
  let aboveTier = 0;
  let totalCommissionAmount = 0;
  let totalCommissionBasis = 0;
  let commissionDeals: CalculationDeal[] = [];

  // Start at default infinite range
  let tierFloor = 0;
  let tierCeiling = Number.POSITIVE_INFINITY;

  // CASE: Floor only
  if (
    (condition.operator === "gt" || condition.operator === "gte") &&
    condition.value
  ) {
    tierFloor = targetValue * condition.value;
  }
  // CASE: Range
  else if (
    condition.operator === "range" &&
    condition.value_from &&
    condition.value_to
  ) {
    tierFloor = targetValue * condition.value_from;
    tierCeiling = targetValue * condition.value_to;
  }
  // CASE: Ceiling only
  else if (
    (condition.operator === "lt" || condition.operator === "lte") &&
    condition.value
  ) {
    tierCeiling = targetValue * condition.value;
  }

  for (const deal of quotaDeals) {
    let dealValue = getCachedDealValue(
      deal,
      data_field,
      company,
      dealValueCache,
      exchangeRates,
      isProjected
    );

    // NOTE: Tendium special case
    if (planId === 384) {
      const contractLength = deal.contract_length_months;
      const invoiceLength = deal.invoice_length_months;
      const termOverBilling = contractLength !== null && invoiceLength !== null
        ? contractLength / invoiceLength
        : null;

      if (contractLength !== null && invoiceLength !== null && termOverBilling !== null) {
        if (termOverBilling <= 1) {
          if (contractLength <= 12) {
            dealValue *= 1;
          } else if (contractLength > 12 && contractLength <= 24) {
            dealValue *= 2;
          } else {
            dealValue *= 3;
          }
        } else {
          if (contractLength <= 12) {
            dealValue *= 1;
          } else if (contractLength > 12 && contractLength <= 24) {
            dealValue *= 1.3;
          } else {
            dealValue *= 1.6;
          }
        }
      }
    }

    const accumulatedTotalAfterThisDealValue = totalDealValue + dealValue;

    // CASE: Below floor even after this deal
    if (accumulatedTotalAfterThisDealValue < tierFloor) {
      belowTier += dealValue;
    }
    // CASE: Already above ceiling before this deal
    else if (totalDealValue > tierCeiling) {
      aboveTier += dealValue;
    }
    // CASE: Partially below tier
    // and partially above tier or fully within tier
    // with this deal
    else {
      // Check if crosses floor. Applies to all tiers except bottom one (where floor is 0)
      const splitsAtFloor = tierFloor > totalDealValue;
      const partialDealValueBelowTier = splitsAtFloor
        ? tierFloor - totalDealValue
        : 0;

      // Value left after floor split
      let remainingDealValue = dealValue - partialDealValueBelowTier;

      // Check if crosses ceiling
      // Applies to all tiers except highest one (where floor is +infinity)
      const splitsAtCeiling = totalDealValue + remainingDealValue > tierCeiling;
      const partialDealValueAboveTier = splitsAtCeiling
        ? totalDealValue + remainingDealValue - tierCeiling
        : 0;

      // Value left after ceiling split
      remainingDealValue -= partialDealValueAboveTier;

      if (criteriaMetDealIds.has(deal.id)) {
        const dealCommission = remainingDealValue * acceleratorRate;
        totalCommissionAmount += dealCommission;
        totalCommissionBasis += remainingDealValue;
        commissionDeals.push(deal);
      }

      belowTier += partialDealValueBelowTier;
      withinTier += remainingDealValue;
      aboveTier += partialDealValueAboveTier;
    }

    // Accumulate
    totalDealValue += dealValue;
  }

  return {
    totalAttainment: totalDealValue,
    commissionAmount: totalCommissionAmount,
    commissionBasis: totalCommissionBasis,
    commissionDeals,
    belowTier,
    withinTier,
    aboveTier,
  };
};

function getProviderStats(deals: CalculationDeal[]) {
  return deals.reduce(
    (acc, deal) => {
      const provider = deal.provider || "unknown";
      if (!acc[provider]) {
        acc[provider] = { count: 0, totalValue: 0 };
      }
      acc[provider].count++;
      acc[provider].totalValue += deal.value || 0;
      return acc;
    },
    {} as Record<string, { count: number; totalValue: number }>
  );
}

// FIXME: Remove this batching nonsense
function precomputeProviderExchangeRates(
  company: NestedCompany,
  deals: CalculationDeal[]
) {
  const rateCache = new Map<string, number>();
  const batchSize = 50;

  // Process exchange rates in batches
  for (let i = 0; i < deals.length; i += batchSize) {
    const batch = deals.slice(i, i + batchSize);
    batch.forEach((deal) => {
      const key = `${deal.provider}:${deal.currency_id || company.default_currency_id}`;
      if (!rateCache.has(key)) {
        rateCache.set(key, getExchangeRateForDeal(company, deal));
      }
    });
  }

  return rateCache;
}

export const calculateCommissionsForPlan = ({
  calculationDate,
  dateRange,
  company,
  user,
  plan,
  isProjected = false,
  logs,
  isDev,
  youniumMetrics,
}: {
  calculationDate: Date;
  dateRange: DateRange;
  company: NestedCompany;
  user: NestedUser;
  plan: NestedPlan;
  isProjected: boolean;
  logs: string[];
  isDev: boolean;
  youniumMetrics?: YouniumMetrics;
}): NestedCommission[] => {
  const planTimer = new OperationTimer("calculateCommissionsForPlan", {
    userId: user.id,
    planId: plan?.id,
    providers: plan?.providers || [],
    dealTypes: plan?.deal_types || [],
  });

  if (!user || !plan || !dateRange) return [];

  const {
    time_period,
    calculation_method,
    deal_types,
    providers,
    accelerators,
    deal_role,
    data_field,
    tendium_csm_plan,
  } = plan || {};

  // Check if we should use this plan for the given date
  if (!shouldCalculate(calculationDate, time_period) || !time_period) {
    // logs.push(
    //   `   ->   Plan ${plan.id} is not active for ${format(calculationDate, "MMMM yyyy", { locale: enUS })}, skipping plan processing.\n`
    // );
    return [];
  }

  // Tendium-only CSM plan logic. Shortcuts the rest of the function.
  if (tendium_csm_plan !== null) {
    if (!youniumMetrics || !youniumMetrics.value) {
      console.warn(`Younium metrics missing. Skipping commissions for plan ${plan.id}.`)
      return [];
    }

    const values = youniumMetrics?.value;
    const openingArr = values.find(v => v.ChangeClassification === "Ingoing Balance")?.Amount ?? 0;
    const upsell = values.find(v => v.ChangeClassification === "Expansion")?.Amount ?? 0;
    const downsell = values.find(v => v.ChangeClassification === "Contraction")?.Amount ?? 0;
    const churn = values.find(v => v.ChangeClassification === "Lost customer")?.Amount ?? 0;

    let csmAttainment = 0;

    if (tendium_csm_plan === "team_grr") {
      csmAttainment = openingArr + downsell + churn; // negative values are already negative so add all
    } else if (tendium_csm_plan === "team_expansion_arr") {
      csmAttainment = upsell + downsell; // negative values are already negative so add all
    }

    const commissions: NestedCommission[] = [];

    for (const accelerator of accelerators) {
      const { success: targetAttained } = criteriaMet({
        calculationDate,
        company,
        user,
        plan,
        accelerator,
        planPeriodDeals: [],
        ytdDeals: [],
        planPeriodCreatedDeals: [],
        ytdCreatedDeals: [],
        csmAttainment,
        logs,
      });

      if (!targetAttained) continue;

      // CSM plans must have fixed value commissions
      if (!accelerator.fixed_value_amount) continue;

      const commission_basis = accelerator.fixed_value_currency_id
        ? getExchangeRateForCurrencyId(
          company,
          accelerator.fixed_value_currency_id
        ) * accelerator.fixed_value_amount
        : accelerator.fixed_value_amount;

      const amount = commission_basis * accelerator.rate;

      const commission: NestedCommission = {
        company_id: company.id,
        month: Number(format(calculationDate, "MM")),
        year: Number(format(calculationDate, "yyyy")),
        user_id: user.id,
        plan_id: plan.id,
        accelerator: {
          id: accelerator.id,
          name: accelerator.name,
          commission_based_on: accelerator.commission_based_on,
          fixed_value_amount: accelerator.fixed_value_amount,
        },
        accelerator_id: accelerator.id,
        rate: accelerator.rate,
        plan: {
          id: plan.id,
          name: plan.name,
          time_period: plan.time_period,
          deal_types: plan.deal_types,
          deal_role: plan.deal_role,
        },
        created_at: new Date() as TODO,
        updated_at: new Date() as TODO,
        status: isProjected ? "projected" : "unapproved",
        deal_id: null,
        deal: undefined,
        commission_basis: commission_basis,
        amount,
        deals_key: "",
        deals_key_hash: "",
        deal_details: [],
      };

      commissions.push(commission);
    }

    return commissions;
  }

  const dealsTimer = new OperationTimer("deals-filtering", {
    userId: user.id,
    dealRole: deal_role,
  });

  // NOTE:
  // Passing YTD deals here as the full deals list
  // It is assumed that all downstream calculations will check their calculation period and filter accordingly
  // (The list may also contain deals for multiple years since YTD is calculated from the app's daterange picker
  // and the user may pick a date range which spans across years)

  // FIXME: This can be removed, since deals are filtered by role down below. Right? Verify
  let deals = filterByDealRole(company.ytdDeals ?? [], deal_role, user.id);

  let createdDeals = company.createdDeals ?? [];

  // Only keep deals which have at least 1 owner with an active license (user.include_in_calculations)
  // or no owners also apparently? Is that right?
  // FIXME: Filter this upstream from the backend when fetching
  const activeLicenseUserIds = new Set(
    company.users
      .filter((user) => user.include_in_calculations)
      .map((user) => user.id)
  );
  deals = deals.filter((deal) => {
    const owners = deal.users?.filter((user) => user.role === "owner");
    if (!owners?.length) return true;
    return owners.some(owner => activeLicenseUserIds.has(owner.user_id))
  });
  createdDeals = createdDeals.filter((deal) => {
    const owners = deal.users?.filter((user) => user.role === "owner");
    if (!owners?.length) return true;
    return owners.some(owner => activeLicenseUserIds.has(owner.user_id))
  });

  const planDateRange = getPlanStartEndDates(
    time_period,
    calculationDate,
    calculation_method
  );
  const ytdDateRange: DateRange = {
    from: startOfYear(planDateRange.from ?? new UTCDate()),
    to: planDateRange.to ?? endOfDay(new UTCDate()),
  };

  const planPeriodDeals: CalculationDeal[] = filterDeals({
    deals,
    dateRange: planDateRange,
    dealTypes: deal_types,
    providers: providers || undefined,
    dealRole: deal_role,
    userId: user.id,
    isProjected,
  });
  const ytdDeals: CalculationDeal[] =
    calculation_method === "arr_equivalent"
      ? planPeriodDeals
      : filterDeals({
        deals,
        dateRange: ytdDateRange,
        dealTypes: deal_types,
        providers: providers || undefined,
        dealRole: deal_role,
        userId: user.id,
        isProjected,
      });

  const planPeriodCreatedDeals: CalculationDeal[] = filterDeals({
    deals: createdDeals,
    dateRange: planDateRange,
    dealTypes: deal_types,
    providers: providers || undefined,
    dealRole: deal_role,
    userId: user.id,
    isProjected,
    filterDateByInvoicedAt: true,
  });
  const ytdCreatedDeals: CalculationDeal[] =
    calculation_method === "arr_equivalent"
      ? planPeriodCreatedDeals
      : filterDeals({
        deals: createdDeals,
        dateRange: ytdDateRange,
        dealTypes: deal_types,
        providers: providers || undefined,
        dealRole: deal_role,
        userId: user.id,
        isProjected,
        filterDateByInvoicedAt: true,
      });

  // Calculate provider stats once
  const providerStatsPeriod = getProviderStats(planPeriodDeals);
  const providerStatsYTD = getProviderStats(ytdDeals);

  logOperation(dealsTimer.end(), logs, isDev);

  let commissions: NestedCommission[] = [];

  // User can now have multiple accelerators per plan that kick in
  // So we need to loop through all of them and check the conditions for each one
  if (!accelerators || accelerators.length === 0) return [];

  // Order accelerators by highest rate
  const sortedAccelerators = accelerators.sort((a, b) => b.rate - a.rate);

  const acceleratorsTimer = new OperationTimer("accelerators-processing", {
    userId: user.id,
    numAccelerators: sortedAccelerators.length,
    totalDeals: planPeriodDeals.length,
    providerStatsPeriod,
    providerStatsYTD,
  });

  // Precompute exchange rates for all deals once
  const exchangeRates = precomputeProviderExchangeRates(company, ytdDeals);

  // Pre-calculate all deal values based on data_field
  const dealValueCache = new Map<string, number>();

  ytdDeals.forEach((deal) => {
    const exchangeRateKey = `${deal.provider}:${deal.currency_id || company.default_currency_id}`;
    const exchangeRate = exchangeRates.get(exchangeRateKey) || 1;

    const dealValueRaw = getDealValueFromDataField(
      deal,
      data_field ?? "value",
      isProjected
    );
    const dealValueAfterExchangeRate = dealValueRaw * exchangeRate;

    const cacheKey = deal.id.toString();
    dealValueCache.set(cacheKey, dealValueAfterExchangeRate);
  });

  logs.push(
    "=".repeat(75),
    // `Commission Calculation for ${getFullName(user)} (${user.email})`,
    `${capitalizeFirstLetter(plan.time_period || "month")}ly Plan: ${plan.name} (Plan ID: ${plan.id})`,
    `Period: ${planDateRange?.from?.toLocaleDateString()} - ${planDateRange?.to?.toLocaleDateString()}`,
    `Found ${planPeriodDeals.length} period deals and ${ytdDeals.length} YTD deals from ${plan?.providers?.join(" and ") || "all providers"} for ${getFullName(user)} as ${deal_role}`,
    "=".repeat(75),
    ""
  );

  const resultLogger = new OperationTimer("Plan Summary", {
    planName: plan.name || "Unnamed Plan",
    userName: getFullName(user),
    period: format(calculationDate, "MMMM yyyy", { locale: enUS }),
  });

  if (planPeriodDeals.length > 0) {
    logs.push("Period Deals:");
    planPeriodDeals.forEach((deal) => {
      const amount = getCurrencyFormatted(
        getDealValueFromDataField(deal, data_field ?? "value", isProjected),
        deal.currency_code
      );
      logs.push(
        `  - ${deal.name} (${format(new UTCDate(deal.closed_at || deal.created_at), "MMM d, yyyy")}): ${amount}`
      );
    });
  }

  if (ytdDeals.length > 0) {
    logs.push("YTD Deals:");
    ytdDeals.forEach((deal) => {
      const amount = getCurrencyFormatted(
        getDealValueFromDataField(deal, data_field ?? "value", isProjected),
        deal.currency_code
      );
      logs.push(
        `  - ${deal.name} (${format(new UTCDate(deal.closed_at || deal.created_at), "MMM d, yyyy")}): ${amount}`
      );
    });
  }

  for (const accelerator of sortedAccelerators) {
    const criteriaLogger = new OperationTimer("criteria-met");

    logs.push(
      `\n`,
      "=".repeat(50),
      `${accelerator.rate * 100}% commission rate for accelerator ${accelerator.id}`,
      "=".repeat(50)
    );

    if (accelerator.conditions && accelerator.conditions.length > 0) {
      logs.push(`Conditions to meet:`);
      accelerator.conditions
        .filter((c) => !c?.key?.includes("quota"))
        .forEach((condition) => {
          logs.push(
            `  -  ${condition.key} ${getHumanReadableConditionName(condition)} (Condition ID: ${condition.id})`
          );
        });
    }

    const {
      success,
      successQuotaTargetValue,
      filteredDeals: criteriaMetDeals,
      quotaDeals,
    } = criteriaMet({
      calculationDate,
      company,
      user,
      plan,
      accelerator,
      planPeriodDeals,
      ytdDeals,
      planPeriodCreatedDeals,
      ytdCreatedDeals,
      csmAttainment: 0, // Not applicable here
      logs,
    });
    logOperation(criteriaLogger.end(), logs, isDev);

    // logs.push(`\nResults:`);
    // logs.push(`  -  Success: ${success}`);
    // logs.push(`  -  SuccessQuotaTargetValue: ${successQuotaTargetValue}`);
    // logs.push(`  -  SuccessQuotaIsYTD: ${successQuotaIsYTD}`);
    // if (success) logs.push(`  -  Deals subject for commission: ${filteredDeals && filteredDeals.length > 0 ? filteredDeals?.map(deal => deal.name) : '(none)'}`);

    if (!success) {
      continue;
    }

    const calculationTimer = new OperationTimer("commission-calculation", {
      userId: user.id,
      numDeals: criteriaMetDeals?.length ?? 0,
    });

    const deals_key = generateCompositeKey(
      criteriaMetDeals?.map((deal) => deal.id) || []
    );
    const deals_key_hash = generateCompositeKeyHash(deals_key);

    const baseCommission: NestedCommission = {
      company_id: company.id,
      month: Number(format(calculationDate, "MM")),
      year: Number(format(calculationDate, "yyyy")),
      user_id: user.id,
      plan_id: plan.id,
      accelerator: {
        id: accelerator.id,
        name: accelerator.name,
        commission_based_on: accelerator.commission_based_on,
        fixed_value_amount: accelerator.fixed_value_amount,
      },
      accelerator_id: accelerator.id,
      rate: accelerator.rate,
      plan: {
        id: plan.id,
        name: plan.name,
        time_period: plan.time_period,
        deal_types: plan.deal_types,
        deal_role: plan.deal_role,
      },
      created_at: new Date() as TODO,
      updated_at: new Date() as TODO,
      status: isProjected ? "projected" : "unapproved",
      deal_id: null,
      deal: undefined,
      commission_basis: 0,
      amount: 0,
      deals_key,
      deals_key_hash,
      deal_details: criteriaMetDeals?.map((d) => ({
        id: d.id,
        name: d.name,
        value: getDealValueFromDataField(d, data_field ?? "value"),
        currency_code: d.currency_code,
      })),
    };

    const criteriaMetDealsTotalValue =
      criteriaMetDeals?.reduce(
        (sum, deal) => sum + (dealValueCache.get(deal.id.toString()) ?? 0),
        0
      ) ?? 0;

    // COMMISSION BASIS: Fixed value
    if (
      accelerator.commission_based_on === "fixed_value" &&
      accelerator.fixed_value_amount
    ) {
      const commission_basis = accelerator.fixed_value_currency_id
        ? getExchangeRateForCurrencyId(
          company,
          accelerator.fixed_value_currency_id
        ) * accelerator.fixed_value_amount
        : accelerator.fixed_value_amount;
      const amount = commission_basis * accelerator.rate;

      // FIXME: Why are we passing criteriaMetDeals related numbers for fixed value commissions?
      const commission: NestedCommission = {
        ...baseCommission,
        commission_basis,
        amount,
        deals_count: criteriaMetDeals?.length || 0,
        value_actual: criteriaMetDealsTotalValue,
        value_target: successQuotaTargetValue,
      };

      if (amount > 0) {
        commissions.push(commission);

        // FIXME: Why are the logs showing criteriaMetDeals numbers for fixed value commissions?
        logs.push(
          "=".repeat(75),
          `SUCCESS: Matched ${accelerator.name} (Accelerator ID: ${accelerator.id})`,
          "=".repeat(75),
          `  -  Based on: ${accelerator.commission_based_on} ${successQuotaTargetValue
            ? `(Actual value this period: ${criteriaMetDealsTotalValue}, Target value: ${successQuotaTargetValue})`
            : ""
          }`,
          `  -  Deals for commission: ${criteriaMetDeals?.length ?? 0} (${criteriaMetDeals?.length && criteriaMetDeals.length <= 10 ? criteriaMetDeals.map((deal) => `${deal.name}: ${getCurrencyFormatted(getDealValue(company, deal), deal.currency_code)}`).join(", ") : criteriaMetDeals?.length && criteriaMetDeals.length > 10 ? "too many deals to list" : ""})`,
          `  -  Total commission basis: ${getCurrencyFormatted(
            commissions.reduce(
              (acc, commission) => acc + commission.commission_basis,
              0
            ),
            company?.default_currency?.code
          )}`,
          `  -  Rate: ${accelerator.rate * 100}%`,
          "=".repeat(75),
          `Amount to be paid: ${getCurrencyFormatted(
            commissions.reduce((acc, commission) => acc + commission.amount, 0),
            company?.default_currency?.code
          )}`,
          "=".repeat(75)
        );
      }
    }
    // COMMISSION BASIS: Fixed amount per deal
    else if (
      accelerator.commission_based_on === "fixed_per_deal" &&
      accelerator.fixed_value_amount
    ) {
      const amount_per_deal = accelerator.fixed_value_currency_id
        ? getExchangeRateForCurrencyId(
          company,
          accelerator.fixed_value_currency_id
        ) * accelerator.fixed_value_amount
        : accelerator.fixed_value_amount;

      const dealCommissions = calculateFixedAmountPerDealCommissions(
        baseCommission,
        criteriaMetDeals ?? [],
        amount_per_deal,
      );
      commissions.push(...dealCommissions);
    }
    // COMMISSION BASIS: Above/below
    else if (
      (accelerator.commission_based_on === "deal_value_above_target" ||
        accelerator.commission_based_on === "deal_value_below_target") &&
      successQuotaTargetValue
    ) {
      const targetValue = successQuotaTargetValue;

      const {
        // totalAttainment,
        commissionAmount: totalCommissionAmount,
        commissionBasis: totalCommissionBasis,
        commissionDeals,
        aboveTarget, // Only used for logging
        belowTarget, // Only used for logging
      } = getAboveBelowCommissionBasis(
        data_field ?? "value",
        accelerator.commission_based_on,
        accelerator.rate,
        targetValue,
        criteriaMetDeals ?? [],
        quotaDeals ?? [],
        company,
        dealValueCache,
        exchangeRates,
        isProjected
      );

      const aboveBelowLogger = new OperationTimer("Above/Below Calculation");
      logOperation(aboveBelowLogger.end(), logs, isDev);

      const commission: NestedCommission = {
        ...baseCommission,
        commission_basis: totalCommissionBasis,
        amount: totalCommissionAmount,
        deals_count: criteriaMetDeals?.length || 0,
        value_actual: criteriaMetDealsTotalValue,
        value_target: successQuotaTargetValue,
      };

      if (totalCommissionAmount > 0) {
        commissions.push(commission);

        logs.push(
          "=".repeat(75),
          `SUCCESS: Matched ${accelerator.name} (Accelerator ID: ${accelerator.id})`,
          "=".repeat(75),
          `  -  Based on: ${accelerator.commission_based_on}`
        );

        logs.push(
          `  -  Target: ${getCurrencyFormatted(targetValue, company?.default_currency?.code)}`
        );
        logs.push(
          `  -  Total Attainment Current Period: ${getCurrencyFormatted(criteriaMetDealsTotalValue, company?.default_currency?.code)}`
        );

        if (
          accelerator.commission_based_on === "deal_value_above_target" &&
          aboveTarget > 0
        ) {
          logs.push(
            `  -  Above target by ${getCurrencyFormatted(aboveTarget, company?.default_currency?.code)}`
          );
        }

        if (
          accelerator.commission_based_on === "deal_value_below_target" &&
          belowTarget > 0
        ) {
          logs.push(
            `  -  Below target by ${getCurrencyFormatted(belowTarget, company?.default_currency?.code)}`
          );
        }

        logs.push(
          `  -  Deals for commission: ${commissionDeals.length} (${commissionDeals.map((deal) => `${deal.name}: ${getCurrencyFormatted(getDealValue(company, deal), company?.default_currency?.code)}`).join(", ")})`,
          `  -  Total commission basis: ${getCurrencyFormatted(totalCommissionBasis, company?.default_currency?.code)}`,
          `  -  Rate: ${accelerator.rate * 100}%`,
          "=".repeat(75),
          `Amount to be paid: ${getCurrencyFormatted(totalCommissionAmount, company?.default_currency?.code)}`,
          "=".repeat(75)
        );
      }
    }
    // COMMISSION BASIS: Tiers
    else if (
      accelerator.commission_based_on === "deal_value_tier" &&
      accelerator.conditions.length > 0 &&
      successQuotaTargetValue
    ) {
      const targetValue = successQuotaTargetValue;

      const {
        // totalAttainment,
        commissionAmount: totalCommissionAmount,
        commissionBasis: totalCommissionBasis,
        commissionDeals,
        belowTier, // Only used for logging
        withinTier, // Only used for logging
        aboveTier, // Only used for logging
      } = getTierCommissionBasis(
        data_field ?? "value",
        accelerator.conditions[0],
        plan.id,
        accelerator.rate,
        targetValue,
        criteriaMetDeals ?? [],
        quotaDeals ?? [],
        company,
        dealValueCache,
        exchangeRates,
        isProjected
      );

      const tierLogger = new OperationTimer("Tier Calculation");
      logOperation(tierLogger.end(), logs, isDev);

      const commission: NestedCommission = {
        ...baseCommission,
        commission_basis: totalCommissionBasis,
        amount: totalCommissionAmount,
        deals_count: criteriaMetDeals?.length || 0,
        value_actual: criteriaMetDealsTotalValue,
        value_target: successQuotaTargetValue,
      };

      if (totalCommissionAmount > 0) {
        commissions.push(commission);

        logs.push(
          "=".repeat(75),
          `SUCCESS: Matched ${accelerator.name} (Accelerator ID: ${accelerator.id})`,
          "=".repeat(75),
          `  -  Based on: ${accelerator.commission_based_on}`
        );

        logs.push(
          `  -  Target: ${getCurrencyFormatted(targetValue, company?.default_currency?.code)}`
        );
        logs.push(
          `  -  Total Attainment Current Period: ${getCurrencyFormatted(criteriaMetDealsTotalValue, company?.default_currency?.code)}`
        );

        if (withinTier > 0) {
          logs.push(
            `  -  Value within tier ${getCurrencyFormatted(withinTier, company?.default_currency?.code)}`
          );
        }

        if (belowTier > 0 || aboveTier > 0) {
          logs.push(
            `  -  Value outside tier ${getCurrencyFormatted(belowTier + aboveTier, company?.default_currency?.code)}`
          );
        }

        logs.push(
          `  -  Deals for commission: ${commissionDeals.length} (${commissionDeals.map((deal) => `${deal.name}: ${getCurrencyFormatted(getDealValue(company, deal), company?.default_currency?.code)}`).join(", ")})`,
          `  -  Total commission basis: ${getCurrencyFormatted(totalCommissionBasis, company?.default_currency?.code)}`,
          `  -  Rate: ${accelerator.rate * 100}%`,
          "=".repeat(75),
          `Amount to be paid: ${getCurrencyFormatted(totalCommissionAmount, company?.default_currency?.code)}`,
          "=".repeat(75)
        );
      }
    }
    // COMMISSION BASIS: Deal value. Data field is provided by the plan (value, onboarding_value, etc)
    else {
      const dealValueLogger = new OperationTimer("Deal Value Calculation");
      logs.push(`\nCalculating Deal Value  commission:`);

      if (accelerator.conditions && accelerator.conditions.length > 0) {
        logs.push(`Conditions required:`);
        accelerator.conditions.forEach((condition) => {
          logs.push(
            `  - ${condition.key} ${condition.operator} ${condition.value}`
          );
        });
      }

      logOperation(dealValueLogger.end(), logs, isDev);

      const dealValueCommissions = calculateDealValueCommissions(
        baseCommission,
        criteriaMetDeals ?? [],
        data_field ?? "value",
        accelerator.rate,
        company,
        dealValueCache,
        exchangeRates,
        isProjected
      );
      commissions.push(...dealValueCommissions);

      logs.push(
        "=".repeat(75),
        `SUCCESS: Matched ${accelerator.name} (Accelerator ID: ${accelerator.id})`,
        "=".repeat(75),
        `  -  Based on: ${accelerator.commission_based_on} ${successQuotaTargetValue
          ? `(Actual value this period: ${criteriaMetDealsTotalValue}, Target value: ${successQuotaTargetValue})`
          : ""
        }`,
        `  -  Deals for commission: ${criteriaMetDeals?.length} (${criteriaMetDeals?.map((deal) => `${deal.name}: ${getCurrencyFormatted(getDealValue(company, deal), company?.default_currency?.code)}`).join(", ")}`,
        `  -  Total commission basis: ${getCurrencyFormatted(
          commissions.reduce(
            (acc, commission) => acc + commission.commission_basis,
            0
          ),
          company?.default_currency?.code
        )}`,
        `  -  Rate: ${accelerator.rate * 100}%`,
        "=".repeat(75),
        `Amount to be paid: ${getCurrencyFormatted(
          commissions.reduce((acc, commission) => acc + commission.amount, 0),
          company?.default_currency?.code
        )}`,
        "=".repeat(75)
      );
    }

    logOperation(calculationTimer.end(), logs, isDev);
  }

  logOperation(acceleratorsTimer.end(), logs, isDev);
  logOperation(resultLogger.end(), logs, isDev);
  logOperation(planTimer.end(), logs, isDev);

  // Add summary to logs
  // logs.push(
  //   "=".repeat(50),
  //   "Final Commission Summary:",
  //   `Total Deals: ${commissions.length}`,
  //   `Total Commission: ${formatCurrency(commissions.reduce((sum, c) => sum + c.amount, 0), 'USD')}`,
  //   "=".repeat(50)
  // );

  return commissions;
};

/*
 * Get cached deal value is exists, otherwise calculate and set in cache
 */
const getCachedDealValue = (
  deal: CalculationDeal,
  data_field: string,
  company: NestedCompany,
  dealValueCache: Map<string, number>,
  exchangeRates: Map<string, number>,
  isProjected: boolean
) => {
  const cacheKey = deal.id.toString();
  const cachedValue = dealValueCache.get(cacheKey);

  if (cachedValue !== undefined) {
    return cachedValue;
  }

  // Calculate again when not found in cache
  const exchangeRateKey = `${deal.provider}:${deal.currency_id || company.default_currency_id}`;
  const exchangeRate =
    exchangeRates.get(exchangeRateKey) || getExchangeRateForDeal(company, deal);

  const dealValueRaw = getDealValueFromDataField(deal, data_field, isProjected);
  const dealValueAfterExchangeRate = dealValueRaw * exchangeRate;

  // Save in cache
  dealValueCache.set(cacheKey, dealValueAfterExchangeRate);

  return dealValueAfterExchangeRate;
};

const calculateDealValueCommissions = (
  baseCommission: NestedCommission,
  deals: CalculationDeal[],
  data_field: string,
  rate: number,
  company: NestedCompany,
  dealValueCache: Map<string, number>,
  exchangeRates: Map<string, number>,
  isProjected: boolean
): NestedCommission[] => {
  return deals.map((deal) => {
    const dealValue = getCachedDealValue(
      deal,
      data_field,
      company,
      dealValueCache,
      exchangeRates,
      isProjected
    );
    const amount = dealValue * rate;
    const deals_key = generateCompositeKey([deal.id]);
    const deals_key_hash = generateCompositeKeyHash(deals_key);

    return {
      ...baseCommission,
      deal_id: deal.id,
      deals_count: 1,
      deals_key,
      deals_key_hash,
      deal: {
        id: deal.id,
        name: deal.name,
        provider: deal.provider,
        provider_id: deal.provider_id,
        is_won: deal.is_won,
        is_lost: deal.is_lost,
        closed_at: deal.closed_at,
        value: deal.value,
      },
      commission_basis: dealValue,
      amount,
    };
  });
};

const calculateFixedAmountPerDealCommissions = (
  baseCommission: NestedCommission,
  deals: CalculationDeal[],
  amount_per_deal: number,
): NestedCommission[] => {
  return deals.map((deal) => {
    const deals_key = generateCompositeKey([deal.id]);
    const deals_key_hash = generateCompositeKeyHash(deals_key);

    return {
      ...baseCommission,
      deal_id: deal.id,
      deals_count: 1,
      deals_key,
      deals_key_hash,
      deal: {
        id: deal.id,
        name: deal.name,
        provider: deal.provider,
        provider_id: deal.provider_id,
        is_won: deal.is_won,
        is_lost: deal.is_lost,
        closed_at: deal.closed_at,
        value: deal.value,
      },
      commission_basis: amount_per_deal,
      amount: amount_per_deal,
    };
  });
};

export const calculateCommissionForUser = ({
  dateRange,
  company,
  user,
  isProjected,
  logs,
  isDev,
  youniumMetrics,
}: {
  dateRange: DateRange;
  company: NestedCompany;
  user: NestedUser;
  isProjected: boolean;
  logs: string[];
  isDev: boolean;
  youniumMetrics?: YouniumMetrics;
}) => {
  const statsTimer = new OperationTimer("initial-provider-stats", {
    userId: user.id,
    totalDeals: company.deals?.length || 0,
  });
  const providerStats = getProviderStats(company.deals || []);
  logOperation(statsTimer.end(), logs, isDev);

  const userTimer = new OperationTimer("calculateCommissionForUser", {
    userId: user.id,
    email: user.email,
    numPlans: user.plans?.length || 0,
    totalDeals: company.deals?.length || 0,
    providerStats,
  });

  const allCommissions: NestedCommission[] = [];

  const { plans: individualPlans, teamPlans } = user;

  const plansFromTeamAssignments: NestedPlan[] =
    teamPlans?.flatMap((tu) =>
      tu.team?.team_plans?.map((tp) => tp.plan) ?? []
    ) ?? [];

  // Merge and deduplicate individual-assigned and team-assigned plans
  const plans: NestedPlan[] = [];
  const seenPlanIds = new Set<number>();

  for (const plan of individualPlans) {
    if (!seenPlanIds.has(plan.id)) {
      seenPlanIds.add(plan.id);
      plans.push(plan);
    }
  }
  for (const plan of plansFromTeamAssignments) {
    if (!seenPlanIds.has(plan.id)) {
      seenPlanIds.add(plan.id);
      plans.push(plan);
    }
  }

  if (plans.length === 0) {
    logOperation(userTimer.end(), logs, isDev);
    return allCommissions;
  }

  const { from, to } = dateRange || {};
  if (!from || !to) {
    logOperation(userTimer.end(), logs, isDev);
    return allCommissions;
  }

  // For the given daterange, we will calculate month by month
  // Explicitly cast dates to UTC so that date-fns handles them as UTC below
  let calculationDateCurrentMonth = new UTCDate(from);
  const periodEnd = new UTCDate(to);

  while (calculationDateCurrentMonth <= periodEnd) {
    const filterTimer = new OperationTimer("period-deal-filtering", {
      userId: user.id,
      period: `${format(calculationDateCurrentMonth, "yyyy-MM")}`,
      totalDeals: company.deals?.length || 0,
    });

    // FIXME: Remove this, not used. Only company.ytdDeals has data now
    const periodDeals = company.deals?.filter((deal) => {
      const dealDate = new UTCDate(deal.closed_at || deal.created_at);
      return isSameMonth(dealDate, calculationDateCurrentMonth);
    });

    logOperation(filterTimer.end(), logs, isDev);

    const periodProviderStats = getProviderStats(periodDeals || []);

    const periodTimer = new OperationTimer("period-processing", {
      userId: user.id,
      period: `${format(calculationDateCurrentMonth, "yyyy-MM")}`,
      numDeals: periodDeals?.length || 0,
      providerStatsPeriod: periodProviderStats,
    });

    logs.push(
      `\n\n`,
      "=".repeat(100),
      `${format(calculationDateCurrentMonth, "MMMM yyyy", { locale: enUS })}: ${getFullName(user)} (${user.email} - ${user.id})`,
      "=".repeat(100),
      `\n\n`
    );

    const planTimer = new OperationTimer("plan-processing", {
      userId: user.id,
      period: `${format(calculationDateCurrentMonth, "yyyy-MM")}`,
      numPlans: plans.length,
      numDeals: periodDeals?.length || 0,
    });

    const plansActiveInCalculationCurrentMonth = plans.filter(plan => {
      const startIsValid = plan.start_date === null ||
        !isBefore(calculationDateCurrentMonth, new UTCDate(plan.start_date));

      const endIsValid = plan.end_date === null ||
        !isAfter(calculationDateCurrentMonth, new UTCDate(plan.end_date));

      return startIsValid && endIsValid;
    })

    const planResults = plansActiveInCalculationCurrentMonth.map((plan) => {
      const commissions = calculateCommissionsForPlan({
        calculationDate: calculationDateCurrentMonth,
        dateRange,
        company,
        user,
        plan,
        isProjected,
        logs,
        isDev,
        youniumMetrics,
      });

      return commissions || [];
    });
    logOperation(planTimer.end(), logs, isDev);

    const aggregationTimer = new OperationTimer("commission-aggregation", {
      userId: user.id,
      period: `${format(calculationDateCurrentMonth, "yyyy-MM")}`,
      numResults: planResults.length,
    });
    planResults.forEach((commissions) => {
      if (commissions && commissions.length > 0) {
        allCommissions.push(...commissions);
      }
    });
    logOperation(aggregationTimer.end(), logs, isDev);

    logOperation(periodTimer.end(), logs, isDev);

    // Increment calculation date by 1 month
    // calculationDate.setMonth(calculationDate.getMonth() + 1);
    calculationDateCurrentMonth = addMonths(calculationDateCurrentMonth, 1);
  }

  logs.push(
    "=".repeat(100),
    `Results for ${getFullName(user)} (${user.email} - ${user.id})`,
    `Period: ${format(dateRange?.from || new Date(), "MMM d, yyyy")} to ${format(dateRange?.to || new Date(), "MMM d, yyyy")}`,
    `Total commission: ${getCurrencyFormatted(getCommissionsValue(allCommissions), company?.default_currency?.code)}`,
    "=".repeat(100)
  );

  const commissionsByMonth = allCommissions.reduce(
    (acc, commission) => {
      const month = format(
        new UTCDate(commission.year, commission.month - 1),
        "MMMM yyyy",
        { locale: enUS }
      );
      acc[month] = (acc[month] || 0) + (commission.amount || 0);
      return acc;
    },
    {} as Record<string, number>
  );

  logs.push(`\nBreakdown by month:`);
  Object.entries(commissionsByMonth).forEach(([month, amount]) => {
    logs.push(
      `  ${month}: ${getCurrencyFormatted(amount, company?.default_currency?.code)}`
    );
  });

  logOperation(userTimer.end(), logs, isDev);

  return allCommissions;
};

export const getCommissionsValue = (commissions: NestedCommission[]) => {
  if (!commissions || commissions?.length === 0) return 0;
  return commissions?.reduce((acc, commission) => acc + commission?.amount, 0);
};

export const getCommissionForDateRange = (
  commissions: NestedCommission[],
  dateRange?: DateRange,
  filterOnStatuses?: CommissionStatus[]
) => {
  const { from, to } = dateRange || {};
  if (!from || !to) return [];

  const startDate = startOfDay(from);
  const endDate = endOfDay(to);

  const statusSet = filterOnStatuses ? new Set(filterOnStatuses) : null;

  const filtered = commissions?.filter((commission) => {
    // FIXME: TEMPORARY
    if (!commission) return false;

    if (statusSet && commission.status && !statusSet.has(commission.status)) {
      return false;
    }

    const commissionDate = new Date(commission.year, commission.month - 1);
    return commissionDate >= startDate && commissionDate <= endDate;
  });

  return filtered;
};

export const getTotalCommissionOfUsers = (users: CommissionUser[]) => {
  return users.reduce((acc, user) => acc + user.value, 0);
};

export const getMonthName = (commission: NestedCommission) => {
  const { year, month } = commission;
  const date = new Date(year, month - 1);
  return format(date, "MMMM", { locale: enUS });
};

export const getUsersForScope = (
  company: SignedInCompany,
  scope?: Scope | null
) => {
  // Handle the case where company might be undefined
  if (!company) return [];

  // Extract users and teams, ensuring users is always an array
  let { users = [] } = company;
  const { teams = [] } = company;

  // Only filter if users is defined and not empty
  if (users && users.length > 0) {
    users = users.filter((user) => user.include_in_calculations);
  } else {
    // Return empty array if no users
    return [];
  }

  // Handle different scope types
  if (!scope) return users;

  if (scope.type === "user") {
    return users.filter((user) => user.id === scope.value);
  }

  if (scope.type === "team") {
    const userIds = getUserIdsForTeam(teams, Number(scope.value));
    return users.filter((user) => userIds.includes(user.id));
  }

  return users;
};

export const getPaidCommissions = (commissions: NestedCommission[]) => {
  return commissions.filter((commission) => commission.status === "paid");
};

export const getKeyDealsFromProjectedCommissions = (
  scopedUsers: NestedUser[],
  groupedCommissions: GroupedCommissions,
  limit: number = 3
): KeyDeal[] => {
  const userMap = new Map(scopedUsers?.map((user) => [user.id, user]));
  const keyDeals: KeyDeal[] = [];

  for (const [_dealKey, commissions] of Object.entries(groupedCommissions)) {
    const firstCommission = commissions?.[0];
    if (!firstCommission) continue;

    const deal = firstCommission.deal;
    if (!deal || deal.is_won || deal.is_lost) continue;

    const owner = userMap.get(firstCommission.user_id);
    if (!owner) continue;

    // FIXME: Only takes the first one. Not precise.
    const dealName = commissions?.[0]?.deal?.name ?? "";
    const planName = commissions?.[0]?.plan?.name;

    const dealValue = getDealValueFromCommissions(commissions);
    const commissionValue = getCommissionsValue(commissions);

    keyDeals.push({
      dealName: dealName || planName || "",
      dealValue,
      dealCurrencyCode: "SEK", // FIXME:
      owner,
      commissionValue,
    });
  }

  return keyDeals
    .sort((a, b) => b.commissionValue - a.commissionValue)
    .slice(0, limit);
};

export const getKeyDealsFromOpenDeals = (deals: NestedDeal[], limit = 3) => {
  const dealsSortedByValue = deals.toSorted((a, b) => b.value - a.value);

  // @ts-ignore
  const keyDeals: KeyDeal[] = dealsSortedByValue.slice(0, limit).map((d) => {
    const owner = d.users?.find((u) => u.role === "owner");

    return {
      dealName: d.name,
      dealValue: d.value,
      dealCurrencyCode: d.currency_code,
      owner: owner?.data,
      commissionValue: 0,
    };
  });

  return keyDeals;
};

export const getCommissionsByQuotaTargetKeyDeals = (
  commissions: NestedCommission[],
  limit: number = 3
): NestedCommission[] => {
  return commissions
    .filter((c) => !c.deal?.is_won)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
};
